import { useEffect, useRef } from 'react';

import type { SupportedLanguage } from '@i18n/config';
import type { InstallLanguageSetting } from '@i18n/installLanguage';

/**
 * Применяет язык интерфейса УСТАНОВКИ (`MAINLANG`) после входа.
 *
 * ⚠️ Живёт в фиче авторизации, а НЕ в `src/i18n/`: слой словарей не должен тянуть за собой
 * API-клиент. Первая редакция лежала в `src/i18n/`, и импорт хука в `AuthProvider` ронял
 * загрузку словарей в двух чужих тестах — экран отрисовывал сырой ключ `settings_main`
 * вместо заголовка. Перенос это снял. Причина внутри графа импортов не установлена, и
 * выдавать «цикл импортов» за диагноз я не буду: проверено переносом, а не разбором, если оператор не выбирал
 * язык сам. Порядок источников и что из эталона отклонено — в `installLanguage.ts`.
 *
 * Вызывается один раз за сессию: повторный запрос ничего не изменит, а лишняя смена языка
 * перерисовывает всё дерево.
 */
export function useInstallLanguage(enabled: boolean): void {
  const done = useRef(false);

  useEffect(() => {
    if (!enabled || done.current) return;
    done.current = true;

    // ЯВНЫЙ выбор оператора бьёт настройку установки — иначе переключатель языка в шапке
    // переставал бы работать на следующей загрузке.
    let explicit: string | null = null;
    try {
      explicit = typeof window !== 'undefined' ? localStorage.getItem('language') : null;
    } catch {
      explicit = null;
    }
    if (explicit) return;

    let cancelled = false;
    void (async () => {
      try {
        // Клиент импортируется ЛЕНИВО. Статический импорт из этого модуля ронял загрузку
        // словарей в двух чужих тестах (`settingsGates`, `TariffGroupDetailPage.addBuilding`):
        // экран отрисовывал сырой ключ `settings_main` вместо заголовка. Запрос всё равно
        // один и после входа, так что ленивая загрузка ничего не стоит.
        const [{ apiClient }, i18nModule, { resolveInstallLanguage }] = await Promise.all([
          import('@shared/api/client'),
          import('@i18n/config'),
          import('@i18n/installLanguage'),
        ]);
        const { default: i18n, isSupportedLanguage, switchLanguage } = i18nModule;
        const { data } = await apiClient.get<InstallLanguageSetting>('/settings/basic/ui-language');
        if (cancelled) return;
        const current = isSupportedLanguage(i18n.language) ? i18n.language : ('ru' as SupportedLanguage);
        const next = resolveInstallLanguage({
          setting: data?.main_lang,
          browser: typeof navigator !== 'undefined' ? navigator.language : undefined,
          current,
        });
        if (next) await switchLanguage(next);
      } catch {
        // Ручка недоступна — интерфейс остаётся на текущем языке. Молча: это не действие
        // оператора, и сообщать ему нечего.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);
}
